import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../../consts';
import { type TransformedAssortmentSkuLocationsItem } from '../../../../redux/assortment/schemas/get-assortment-sku-locations.schema';

const COLUMN_ID = 'autone_recommendation' as const;

export type AutoneRecommendationColumnId = typeof COLUMN_ID;

interface AutoneRecommendationFields {
  is_assorted: TransformedAssortmentSkuLocationsItem['is_assorted'];
  system_assortment_recommendation: TransformedAssortmentSkuLocationsItem['system_assortment_recommendation'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<AutoneRecommendationFields>();

export const createAutoneRecommendationColumn = <
  T extends AutoneRecommendationFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => {
      return row.system_assortment_recommendation;
    }),
    {
      id: COLUMN_ID,
      size: 190,
      header: function AutoneRecommendationHeader() {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableCellContent align="left">
            <DataTableCellTitle className="uppercase font-medium whitespace-normal">
              {t('table-columns.autone-recommendation')}
            </DataTableCellTitle>
          </DataTableCellContent>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function AutoneRecommendationCell({ row }) {
            const { t } = useAutoneTranslation<typeof APP_NAME>();
            const { is_assorted, system_assortment_recommendation } =
              row.original;

            const text = useMemo(() => {
              if (system_assortment_recommendation === 'hold') {
                return is_assorted.value
                  ? t('assortment-statuses.recommended-on')
                  : t('assortment-statuses.recommended-off');
              }
              return system_assortment_recommendation === 'assort'
                ? t('assortment-statuses.recommended-on')
                : t('assortment-statuses.recommended-off');
            }, [system_assortment_recommendation, is_assorted.value, t]);

            return (
              <DataTableCellContent
                align="left"
                className="flex flex-row gap-2 items-center"
              >
                {text}
              </DataTableCellContent>
            );
          },
        }),
    },
  );
